import React, { useState, useMemo } from 'react';
import { ICON } from '../icons/iconIds';
import IconText from './icons/IconText';
import {
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import Card from './ui/Card';
import Button from './ui/Button';
import Spinner from './ui/Spinner';
import { rowPassesVizFilters } from '../utils/coringActivityHelpers';
import './CoringActivity.css';

// Color palette for rigs
const RIG_COLORS = {
  'Rig 9': '#3b82f6',
  'Rig 10': '#f97316',
  'Rig 11': '#22c55e',
  'Rig 12': '#ec4899',
  'Rig 13': '#8b5cf6',
  'Rig 14': '#14b8a6',
  'Rig 15': '#eab308',
  'Rig 16': '#ef4444'
};

// Color palette for shifts
const SHIFT_COLORS = {
  'Day': '#f59e0b',
  'Night': '#3b82f6'
};

const CoringActivity = ({
  holeFilters,
  data = [],
  loading = false,
  error = null,
  dateRange = 'month',
  setDateRange,
  customStartDate = '',
  setCustomStartDate,
  customEndDate = '',
  setCustomEndDate,
  searchTerm = '',
  setSearchTerm
}) => {
  const [groupBy, setGroupBy] = useState('drill'); // 'drill' or 'shift'

  // Filter data based on search and hole filters from parent dashboard (same rules as Coring Data Explorer)
  const filteredData = useMemo(() => {
    return data.filter((row) => rowPassesVizFilters(row, holeFilters, searchTerm));
  }, [data, searchTerm, holeFilters]);

  // Process data for chart
  const chartData = useMemo(() => {
    // Group by date and groupBy field (drill or shift)
    const grouped = {};
    
    filteredData.forEach(row => {
      const date = row.report_date;
      const groupKey = row[groupBy]; // 'drill' or 'shift'
      
      if (!grouped[date]) {
        grouped[date] = { date };
      }
      
      if (!grouped[date][groupKey]) {
        grouped[date][groupKey] = 0;
      }
      
      grouped[date][groupKey] += parseFloat(row.penetration) || 0;
    });
    
    // Convert to array and sort by date
    const chartArray = Object.values(grouped).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    
    // Add cumulative total
    let cumulative = 0;
    chartArray.forEach(item => {
      const dailyTotal = Object.keys(item)
        .filter(key => key !== 'date' && key !== 'cumulative')
        .reduce((sum, key) => sum + (item[key] || 0), 0);
      
      cumulative += dailyTotal;
      item.cumulative = cumulative;
    });
    
    return chartArray;
  }, [filteredData, groupBy]);

  // Get unique groups for legend
  const uniqueGroups = useMemo(() => {
    const groups = new Set();
    filteredData.forEach(row => {
      if (row[groupBy]) {
        groups.add(row[groupBy]);
      }
    });
    return Array.from(groups).sort();
  }, [filteredData, groupBy]);

  // Get color for group
  const getColor = (group) => {
    if (groupBy === 'drill') {
      return RIG_COLORS[group] || '#6b7280';
    } else {
      return SHIFT_COLORS[group] || '#6b7280';
    }
  };

  // Calculate summary metrics
  const metrics = useMemo(() => {
    const totalPenetration = filteredData.reduce((sum, row) => 
      sum + (parseFloat(row.penetration) || 0), 0
    );
    const totalHours = filteredData.reduce((sum, row) => 
      sum + (parseFloat(row.activity_hours) || 0), 0
    );
    const uniqueHoles = new Set(filteredData.map(row => row.hole_name)).size;
    const avgPenetrationPerDay = totalPenetration / (chartData.length || 1);
    const avgPenetrationPerHour = totalHours > 0 ? totalPenetration / totalHours : 0;
    
    // Debug: Show filtering results
    if (filteredData.length !== data.length) {
      const removedCount = data.length - filteredData.length;
      console.log('🔍 Filters active - showing', filteredData.length, 'of', data.length, 'records');
      console.log('📊 Filtered data:', filteredData.length, 'records,', uniqueHoles, 'unique holes');
      console.log('⚠️ Filtered out:', removedCount, 'records due to dashboard filters or search');
    } else {
      console.log('✅ Showing all data:', filteredData.length, 'records,', uniqueHoles, 'unique holes');
    }
    
    return {
      totalPenetration,
      totalHours,
      uniqueHoles,
      avgPenetrationPerDay,
      avgPenetrationPerHour,
      recordCount: filteredData.length
    };
  }, [filteredData, chartData, data.length]);

  // Helper function to format date string without timezone issues
  // Treats the date as local (e.g., "2025-12-12" stays as December 12, not shifted by timezone)
  const formatDateLocal = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const date = formatDateLocal(label);
      const cumulative = payload.find(p => p.dataKey === 'cumulative');
      
      return (
        <div className="custom-tooltip">
          <p className="label">{date}</p>
          {payload
            .filter(p => p.dataKey !== 'cumulative')
            .map((entry, index) => (
              <p key={index} style={{ color: entry.color }}>
                {entry.name}: {entry.value.toFixed(1)}m
              </p>
            ))}
          {cumulative && (
            <p style={{ color: cumulative.color, fontWeight: 'bold', borderTop: '1px solid #374151', paddingTop: '4px', marginTop: '4px' }}>
              Cumulative: {cumulative.value.toFixed(1)}m
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="coring-loading">
        <Spinner size="large" />
        <h2>Loading Coring Activity...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <Card variant="elevated" className="error-card">
        <div className="error-content">
          <IconText icon={ICON.uiError} as="h3">Failed to Load Coring Activity</IconText>
          <p className="error-message">{error}</p>
          <Button 
            variant="primary" 
            onClick={() => window.location.reload()}
            style={{ marginTop: '1rem' }}
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="coring-activity">
      {/* Controls */}
      <Card variant="elevated" style={{ padding: '1rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          {/* Date Range Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <IconText icon={ICON.uiCalendarDate} iconSize={14} as="span" className="coring-period-label">Period:</IconText>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              {['week', 'month', 'year', 'all', 'custom'].map(range => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid',
                    borderColor: dateRange === range ? '#f9be0b' : '#374151',
                    background: dateRange === range ? 'rgba(249, 190, 11, 0.1)' : '#1f2937',
                    color: dateRange === range ? '#f9be0b' : '#9ca3af',
                    fontSize: '0.875rem',
                    fontWeight: dateRange === range ? '600' : '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textTransform: 'capitalize'
                  }}
                >
                  {range === 'all' ? 'All Time' : range}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date Range */}
          {dateRange === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                style={{
                  padding: '0.5rem',
                  background: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#e5e7eb',
                  fontSize: '0.875rem'
                }}
              />
              <span style={{ color: '#9ca3af' }}>to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                style={{
                  padding: '0.5rem',
                  background: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#e5e7eb',
                  fontSize: '0.875rem'
                }}
              />
            </div>
          )}

          {/* Group By Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Group by:</span>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              {['drill', 'shift'].map(group => (
                <button
                  key={group}
                  onClick={() => setGroupBy(group)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid',
                    borderColor: groupBy === group ? '#f9be0b' : '#374151',
                    background: groupBy === group ? 'rgba(249, 190, 11, 0.1)' : '#1f2937',
                    color: groupBy === group ? '#f9be0b' : '#9ca3af',
                    fontSize: '0.875rem',
                    fontWeight: groupBy === group ? '600' : '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textTransform: 'capitalize'
                  }}
                >
                  {group === 'drill' ? (<IconText icon={ICON.drillFilterRig} iconSize={14} as="span">Drill Rig</IconText>) : (<IconText icon={ICON.uiShift} iconSize={14} as="span">Shift</IconText>)}
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search hole..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '0.5rem 0.75rem',
              background: '#1f2937',
              border: '1px solid #374151',
              borderRadius: '6px',
              color: '#e5e7eb',
              fontSize: '0.875rem',
              minWidth: '200px'
            }}
          />
        </div>
      </Card>

      {/* Date Range Display */}
      {data.length > 0 && (
        <Card variant="elevated" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', background: '#1f2937', border: '1px solid #374151' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.875rem' }}>
            <span style={{ color: '#9ca3af' }}>
              <IconText icon={ICON.uiChartAnalytics} iconSize={14} as="span">
                Showing {filteredData.length} of {data.length} records
              </IconText>
            </span>
            <span style={{ color: '#9ca3af' }}>
              <IconText icon={ICON.uiCalendarDate} iconSize={14} as="span">Date Range:</IconText> {(() => {
                const dates = data.map(r => r.report_date).sort();
                return `${dates[0]} to ${dates[dates.length - 1]}`;
              })()}
            </span>
          </div>
        </Card>
      )}

      {/* Metrics Summary */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <Card variant="elevated" style={{ flex: 1, minWidth: '150px', padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#f9be0b' }}>
            {metrics.totalPenetration.toFixed(0)}m
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px' }}>
            Total Penetration
          </div>
        </Card>
        
        <Card variant="elevated" style={{ flex: 1, minWidth: '150px', padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#3b82f6' }}>
            {metrics.uniqueHoles}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px' }}>
            Unique Holes
          </div>
        </Card>
        
        <Card variant="elevated" style={{ flex: 1, minWidth: '150px', padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#10b981' }}>
            {metrics.avgPenetrationPerDay.toFixed(1)}m
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px' }}>
            Avg Per Day
          </div>
        </Card>
        
        <Card variant="elevated" style={{ flex: 1, minWidth: '150px', padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#ec4899' }}>
            {metrics.avgPenetrationPerHour.toFixed(1)}m/hr
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px' }}>
            Avg Rate
          </div>
        </Card>
        
        <Card variant="elevated" style={{ flex: 1, minWidth: '150px', padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#8b5cf6' }}>
            {metrics.totalHours.toFixed(0)}hrs
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px' }}>
            Total Hours
          </div>
        </Card>
      </div>

      {/* Chart */}
      <Card variant="elevated" style={{ padding: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: '#f9fafb' }}>
          <IconText icon={ICON.uiChartAnalytics} iconSize={14}>
            Daily Production ({dateRange === 'custom' ? 'Custom Range' : dateRange})
          </IconText>
        </h3>
        
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={500}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="date" 
                stroke="#9ca3af"
                tick={{ fill: '#9ca3af', fontSize: 12 }}
                tickFormatter={(date) => formatDateLocal(date)}
              />
              <YAxis 
                yAxisId="left"
                stroke="#9ca3af"
                tick={{ fill: '#9ca3af', fontSize: 12 }}
                label={{ value: 'Penetration (m)', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                stroke="#f9be0b"
                tick={{ fill: '#f9be0b', fontSize: 12 }}
                label={{ value: 'Cumulative (m)', angle: 90, position: 'insideRight', fill: '#f9be0b' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="square"
              />
              
              {/* Stacked bars for each group */}
              {uniqueGroups.map((group, index) => (
                <Bar
                  key={group}
                  yAxisId="left"
                  dataKey={group}
                  stackId="a"
                  fill={getColor(group)}
                  name={group}
                />
              ))}
              
              {/* Cumulative line */}
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="cumulative"
                stroke="#f9be0b"
                strokeWidth={3}
                dot={{ fill: '#f9be0b', r: 4 }}
                name="Cumulative Total"
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
            <p>No coring activity data available for the selected period.</p>
            <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Try adjusting your date range or filters.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CoringActivity;

